import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { ScaledIframe } from "newui/components/ScaledIframe";
import { PreviewPageCard } from "newui/components/PreviewPageCard";
import PdfPreviewZoomBar from "newui/components/PdfPreviewZoomBar";
import { useShortlistPreviewHtml } from "../../hooks/useShortlistPreviewHtml";
import type { ShortlistPreviewHtmlResponse } from "../../api/shortlist-report-api";
import type { SectionVisibility, FieldVisibility } from "../../hooks/useShortlistReportState";

interface ShortlistPreviewPanelProps {
	additionalText?: string;
	companyIds?: number[];
	coverCreatedByRole?: string;
	coverPageMode?: string;
	coverPresentedTo?: string;
	coverPresentedToRole?: string;
	coverProjectName?: string;
	coverShowLogo?: boolean | null;
	coverSupplierRole?: string;
	fields: FieldVisibility;
	groupId?: number | string;
	includeAdditionalText?: boolean;
	includeProjectPage?: boolean;
	projectDescription?: string;
	projectId?: number | null;
	projectVisibleFields?: Record<string, boolean>;
	searchSectorIds?: number[];
	sections: SectionVisibility;
}

const DEBOUNCE_MS = 300;
const A4_LANDSCAPE_WIDTH = 1123;
const A4_LANDSCAPE_HEIGHT = 794;
const A4_PORTRAIT_WIDTH = 794;
const A4_PORTRAIT_HEIGHT = 1123;
const PREVIEW_DEFAULT_WIDTH = 560;
const PREVIEW_MIN_WIDTH = 320;
const PREVIEW_MAX_WIDTH = 1200;

/* ---- Main panel ---- */

export const ShortlistPreviewPanel: React.FC<ShortlistPreviewPanelProps> = ({
	sections, fields, companyIds, searchSectorIds, groupId,
	additionalText, includeAdditionalText, projectId, projectDescription,
	includeProjectPage, projectVisibleFields,
	coverProjectName, coverSupplierRole, coverPresentedTo, coverPresentedToRole,
	coverShowLogo, coverCreatedByRole, coverPageMode,
}) => {
	const previewMutation = useShortlistPreviewHtml();
	const [previewData, setPreviewData] = useState<ShortlistPreviewHtmlResponse | null>(null);
	const [previewWidth, setPreviewWidth] = useState(PREVIEW_DEFAULT_WIDTH);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const prevFingerprintRef = useRef<string>("");
	const mutateRef = useRef(previewMutation.mutate);
	mutateRef.current = previewMutation.mutate;

	// Build fingerprint from all parameters that affect preview content
	const fingerprint = useMemo(() => {
		return JSON.stringify({
			sections, fields, searchSectorIds, companyIds, groupId,
			additionalText: includeAdditionalText ? additionalText : undefined,
			projectId: includeProjectPage ? projectId : undefined,
			projectDescription: includeProjectPage ? projectDescription : undefined,
			includeProjectPage,
			projectVisibleFields: includeProjectPage ? projectVisibleFields : undefined,
			coverProjectName, coverSupplierRole, coverPresentedTo, coverPresentedToRole,
			coverShowLogo, coverCreatedByRole, coverPageMode,
		});
	}, [sections, fields, searchSectorIds, companyIds, groupId, additionalText, includeAdditionalText, projectId, projectDescription, includeProjectPage, projectVisibleFields, coverProjectName, coverSupplierRole, coverPresentedTo, coverPresentedToRole, coverShowLogo, coverCreatedByRole, coverPageMode]);

	// Derive visible_sections and visible_fields from state
	const buildParams = useCallback(() => {
		const visibleSections = Object.entries(sections)
			.filter(([, v]) => v !== false)
			.map(([k]) => k);

		const visibleFields: Record<string, string[]> = {};
		for (const [sectionKey, fieldMap] of Object.entries(fields)) {
			if (sections[sectionKey] === false) {
				continue;
			}
			const activeFields = Object.entries(fieldMap)
				.filter(([, v]) => v !== false)
				.map(([k]) => k);
			if (activeFields.length > 0) {
				visibleFields[sectionKey] = activeFields;
			}
		}

		return {
			visible_sections: visibleSections.length > 0 ? visibleSections : undefined,
			visible_fields: Object.keys(visibleFields).length > 0 ? visibleFields : undefined,
			company_ids: companyIds && companyIds.length > 0 ? companyIds : undefined,
			search_sector_ids: searchSectorIds && searchSectorIds.length > 0 ? searchSectorIds : undefined,
			group_id: groupId || undefined,
			additional_text: includeAdditionalText ? additionalText || undefined : undefined,
			project_id: includeProjectPage ? projectId || undefined : undefined,
			project_description_override: includeProjectPage && projectDescription ? projectDescription : undefined,
			include_project_details: includeProjectPage || undefined,
			project_visible_fields: includeProjectPage && projectVisibleFields ? projectVisibleFields : undefined,
			cover_project_name: coverProjectName || undefined,
			cover_supplier_role: coverSupplierRole || undefined,
			cover_presented_to: coverPresentedTo || undefined,
			cover_presented_to_role: coverPresentedToRole || undefined,
			cover_show_logo: coverShowLogo !== null ? coverShowLogo : undefined,
			cover_created_by_role: coverCreatedByRole || undefined,
			cover_page_mode: coverPageMode && coverPageMode !== "global" ? coverPageMode : undefined,
		};
	}, [sections, fields, companyIds, searchSectorIds, groupId, additionalText, includeAdditionalText, projectId, projectDescription, includeProjectPage, projectVisibleFields, coverProjectName, coverSupplierRole, coverPresentedTo, coverPresentedToRole, coverShowLogo, coverCreatedByRole, coverPageMode]);

	const fetchPreview = useCallback(() => {
		const params = buildParams();
		mutateRef.current(params, {
			onSuccess: (data) => setPreviewData(data),
		});
	}, [buildParams]);

	// Fetch on mount and debounce on changes
	useEffect(() => {
		if (prevFingerprintRef.current === "") {
			prevFingerprintRef.current = fingerprint;
			fetchPreview();
			return;
		}

		if (prevFingerprintRef.current !== fingerprint) {
			prevFingerprintRef.current = fingerprint;
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
			debounceRef.current = setTimeout(fetchPreview, DEBOUNCE_MS);
		}

		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		};
	}, [fingerprint, fetchPreview]);

	if (!previewData) {
		return (
			<div className="flex flex-col items-center justify-center py-12">
				<Spinner size="md" className="mb-3" color="blue.500" />
				<span className="text-sm text-gray-400">Loading preview...</span>
			</div>
		);
	}

	const hasCoverPage = !!previewData.cover_html && previewData.cover_html.length > 0;
	const hasAdditionalText = !!previewData.additional_text_html;
	const hasProjectDetails = !!previewData.project_details_html;
	const showHeader = !!previewData.header_html;
	const showFooter = !!previewData.footer_html;
	const totalPages = (hasCoverPage ? 1 : 0) + (hasAdditionalText ? 1 : 0) + (hasProjectDetails ? 1 : 0) + previewData.pages.length;
	let pageCounter = 0;

	return (
		<><PdfPreviewZoomBar currentPreviewWidth={previewWidth} previewDefaultWidth={PREVIEW_DEFAULT_WIDTH} previewMaxWidth={PREVIEW_MAX_WIDTH} previewMinWidth={PREVIEW_MIN_WIDTH} totalPages={totalPages} setPreviewWidth={setPreviewWidth} showSampleDataBanner={!companyIds || companyIds.length === 0} />
			<div className="overflow-x-auto">
				<div className="flex flex-col" style={{ minWidth: "fit-content" }}>

					{/* Pages */}
					<div className="flex flex-col gap-4">
						{/* Cover page — portrait */}
						{hasCoverPage && (
							<PreviewPageCard pageLabel={`Page ${++pageCounter} — Cover Page`} previewWidth={previewWidth}>
								<ScaledIframe
									srcDoc={previewData.cover_html!}
									title="Cover page preview"
									nativeWidth={A4_PORTRAIT_WIDTH}
									previewWidth={previewWidth}
									minHeight={A4_PORTRAIT_HEIGHT}
								/>
							</PreviewPageCard>
						)}

						{/* Additional text page — portrait, after cover */}
						{hasAdditionalText && (
							<PreviewPageCard pageLabel={`Page ${++pageCounter} — Additional Text`} previewWidth={previewWidth}>
								<ScaledIframe
									srcDoc={previewData.additional_text_html!}
									title="Additional text preview"
									nativeWidth={A4_PORTRAIT_WIDTH}
									previewWidth={previewWidth}
									minHeight={A4_PORTRAIT_HEIGHT}
								/>
							</PreviewPageCard>
						)}

						{/* Project details page — portrait, after additional text */}
						{hasProjectDetails && (
							<PreviewPageCard pageLabel={`Page ${++pageCounter} — Project Details`} previewWidth={previewWidth}>
								<ScaledIframe
									srcDoc={previewData.project_details_html!}
									title="Project details preview"
									nativeWidth={A4_PORTRAIT_WIDTH}
									previewWidth={previewWidth}
									minHeight={A4_PORTRAIT_HEIGHT}
								/>
							</PreviewPageCard>
						)}

						{/* Report pages — landscape */}
						{previewData.pages.map((page, index) => {
							const pageNum = pageCounter + 1 + index;
							return (
								<PreviewPageCard key={page.label} pageLabel={`Page ${pageNum} — ${page.label}`} previewWidth={previewWidth}>
									{showHeader && (
										<ScaledIframe
											srcDoc={previewData.header_html!}
											title={`Header page ${pageNum}`}
											nativeWidth={A4_LANDSCAPE_WIDTH}
											previewWidth={previewWidth}
											fixedHeight={40}
										/>
									)}
									<ScaledIframe
										srcDoc={page.html}
										title={`${page.label} preview`}
										nativeWidth={A4_LANDSCAPE_WIDTH}
										previewWidth={previewWidth}
										minHeight={A4_LANDSCAPE_HEIGHT}
									/>
									{showFooter && (
										<ScaledIframe
											srcDoc={previewData.footer_html!}
											title={`Footer page ${pageNum}`}
											nativeWidth={A4_LANDSCAPE_WIDTH}
											previewWidth={previewWidth}
											fixedHeight={36}
										/>
									)}
								</PreviewPageCard>
							);
						})}
					</div>
				</div>
			</div>
		</>
	);
};
